<template>
    <VMultiselect
        v-model="valueInternal"
        :options="optionsFiltered"
        track-by="id"
        label="name"
        :placeholder="isPlaceholderVisible ? placeholder : ''"
        :multiple="multiple"
        :allow-empty="multiple"
        :max="optionsFiltered.length || null"
        :close-on-select="!multiple"
        :hide-selected="multiple"
        :show-labels="false"
        :internal-search="!showDetails"
        @search-change="handleSearchQueryChange"
    >
        <template
            v-if="showDetails"
            #singleLabel="{ option: { name, account_details: { name: account_name } } }"
        >
            <div class="option">
                <span>
                    <strong>{{ name }}</strong>
                </span>

                <span>{{ account_name }}</span>
            </div>
        </template>

        <template
            v-if="showDetails"
            #option="{ option: { username, account_details: { name: account_name } } }"
        >
            <div class="option">
                <span>
                    <strong>{{ username }}</strong>
                </span>

                <span>{{ account_name }}</span>
            </div>
        </template>

        <template #noOptions>
            {{ $t('noOptions') }}
        </template>

        <template #noResult>
            {{ $t('noResult') }}
        </template>

        <template #maxElements>
            {{ $t('noOptions') }}
        </template>
    </VMultiselect>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import VMultiselect from 'vue-multiselect'

export default {
    name: 'RoleSelect',
    components: {
        VMultiselect,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        exclude: {
            type: Array,
            default: () => [],
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: null,
        },
        showDetails: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Number, Array],
            default: null,
        },
    },
    data() {
        return {
            searchQuery: '',
        }
    },
    computed: {
        ...mapState('auth', ['userInfo']),
        ...mapGetters('auth', ['isAccountAdmin']),
        ...mapGetters('role', ['rolesSortedByName']),
        isPlaceholderVisible() {
            return this.multiple
                ? !this.valueInternal?.length
                : !this.valueInternal
        },
        optionsAvailable() {
            return this.rolesSortedByName.filter(
                role =>
                    !this.exclude.includes(role.id) &&
                    (!this.isAdmin || this.userInfo.account === role.account)
            )
        },
        optionsFiltered() {
            return this.searchQuery
                ? this.optionsAvailable.filter(
                      option =>
                          option.name
                              .toLowerCase()
                              .includes(this.searchQuery) ||
                          !option.account_details?.name ||
                          option.account_details.name
                              .toLowerCase()
                              .includes(this.searchQuery)
                  )
                : this.optionsAvailable
        },
        valueInternal: {
            get() {
                if (!this.value) {
                    return null
                }
                return this.multiple
                    ? this.optionsAvailable.filter(({ id }) =>
                          this.value.includes(id)
                      )
                    : this.optionsAvailable.find(({ id }) => this.value === id)
            },
            set(value) {
                this.$emit(
                    'input',
                    this.multiple ? value.map(item => item.id) : value.id
                )
            },
        },
    },
    methods: {
        handleSearchQueryChange(query) {
            if (this.showDetails) {
                this.searchQuery = query.trim().toLowerCase()
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "noOptions": "No roles available",
        "noResult": "No roles were found"
    },
    "de": {
        "noOptions": "Keine Rollen verfügbar",
        "noResult": "Kein Rollen gefunden"
    },
    "fr": {
        "noOptions": "Aucun rôle disponible",
        "noResult": "Aucun rôle n'a été trouvé"
    },
    "it": {
        "noOptions": "Nessun ruoli disponibile",
        "noResult": "Nessun ruoli è stato trovato"
    }
}
</i18n>

<style lang="scss" scoped>
.option {
    display: flex;
    justify-content: space-between;

    span {
        &:first-child {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        &:last-child {
            margin-left: 1em;
            font-weight: 700;
            opacity: 0.5;
            white-space: nowrap;
        }
    }

    strong {
        margin-right: 0.5em;
    }
}
</style>
